import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { useNavigate } from 'react-router-dom';

export default function Feedback() {
  const { t, l } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    feedback_type: 'suggestion',
    subject: '',
    message: '',
    contact_email: user?.email || '',
    contact_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return
    setFormData((prev) => ({
      ...prev,
      contact_email: prev.contact_email || user.email || '',
      contact_name:
        prev.contact_name ||
        `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
        user.username ||
        '',
    }))
  }, [user])

  const feedbackTypes = [
    { value: 'suggestion', label: l('Suggestion', 'အကြံပြုချက်', '建议') },
    { value: 'bug_report', label: l('Bug Report', 'ချို့ယွင်းချက် အစီရင်ခံ', '错误报告') },
    { value: 'feature_request', label: l('Feature Request', 'အင်္ဂါရပ် တောင်းဆိုချက်', '功能请求') },
    { value: 'general', label: l('General Feedback', 'ယေဘုယျ တုံ့ပြန်ချက်', '一般反馈') },
    { value: 'other', label: l('Other', 'အခြား', '其他') }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const payload = {
        feedback_type: formData.feedback_type,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        ...(formData.contact_email && { contact_email: formData.contact_email.trim() }),
        ...(formData.contact_name && { contact_name: formData.contact_name.trim() })
      };

      await api.submitFeedback(payload);
      setSubmitted(true);
      setFormData({
        feedback_type: 'suggestion',
        subject: '',
        message: '',
        contact_email: user?.email || '',
        contact_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''
      });

      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-card rounded-2xl p-12 border border-border shadow-lg">
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4">
                <span className="text-4xl">✓</span>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              {l('Thank You!', 'ကျေးဇူးတင်ပါသည်!', '谢谢！')}
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              {l(
                'Your feedback has been submitted successfully. We appreciate your input and will review it soon.',
                'သင်၏ တုံ့ပြန်ချက်ကို အောင်မြင်စွာ ပေးပို့ပြီးပါပြီ။ ကျွန်ုပ်တို့သည် သင်၏ အကြံပြုချက်ကို တန်ဖိုးထားပြီး မကြာမီ ပြန်လည် စစ်ဆေးပါမည်။',
                '您的反馈已成功提交。我们感谢您的意见，将尽快审阅。'
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {l(
                'Redirecting to home page...',
                'မူလစာမျက်နှာသို့ ပြန်လည် ညွှန်ပြနေသည်...',
                '正在跳转到首页...'
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-card rounded-2xl shadow-lg p-6 md:p-8 lg:p-10 border border-border">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              {l('Share Your Feedback', 'သင်၏ တုံ့ပြန်ချက်ကို မျှဝေပါ', '分享您的反馈')}
            </h1>
            <p className="text-muted-foreground">
              {l(
                'We value your input! Help us improve Cosmic Learning by sharing your thoughts, suggestions, or reporting issues. Your feedback is optional and contact details are not required.',
                'ကျွန်ုပ်တို့သည် သင်၏ အကြံပြုချက်ကို တန်ဖိုးထားပါသည်! သင်၏ စိတ်ကူးများ၊ အကြံပြုချက်များ သို့မဟုတ် ပြဿနာများကို အစီရင်ခံခြင်းဖြင့် Cosmic Learning ကို မြှင့်တင်ရန် အကူအညီပေးပါ။ သင်၏ တုံ့ပြန်ချက်သည် ရွေးချယ်စရာ ဖြစ်ပြီး ဆက်သွယ်ရန် အသေးစိတ်များ မလိုအပ်ပါ။',
                '我们重视您的意见！请分享想法、建议或报告问题，帮助我们改进 Cosmic Learning。反馈为可选，无需提供联系方式。'
              )}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Feedback Type */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                {l('Feedback Type', 'တုံ့ပြန်ချက် အမျိုးအစား', '反馈类型')} <span className="text-destructive">*</span>
              </label>
              <select
                name="feedback_type"
                value={formData.feedback_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {feedbackTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                {l('Subject', 'ခေါင်းစဉ်', '主题')} <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                maxLength={200}
                placeholder={l('Brief summary of your feedback', 'သင်၏ တုံ့ပြန်ချက်၏ အတိုချုပ်', '简要概括您的反馈')}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                {l('Message', 'မက်ဆေ့ချ်', '消息')} <span className="text-destructive">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={8}
                maxLength={2000}
                placeholder={l(
                  'Please share your thoughts, suggestions, or describe any issues you\'ve encountered...',
                  'ကျေးဇူးပြု၍ သင်၏ စိတ်ကူးများ၊ အကြံပြုချက်များ သို့မဟုတ် သင်ကြုံတွေ့ရသော ပြဿနာများကို ဖော်ပြပါ...',
                  '请分享您的想法、建议，或描述您遇到的问题...'
                )}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.message.length}/2000 {l('characters', 'စာလုံးများ', '字符')}
              </p>
            </div>

            {/* Optional Contact Details */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {l('Contact Details (Optional)', 'ဆက်သွယ်ရန် အသေးစိတ်များ (ရွေးချယ်စရာ)', '联系方式（可选）')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {l(
                  'If you\'d like us to follow up with you, please provide your contact information. This is completely optional.',
                  'ကျွန်ုပ်တို့က သင့်အား ဆက်သွယ်လိုပါက ကျေးဇူးပြု၍ သင်၏ ဆက်သွယ်ရန် အချက်အလက်များကို ပေးပါ။ ဤအရာသည် လုံးဝ ရွေးချယ်စရာ ဖြစ်ပါသည်။',
                  '如需我们跟进，请提供联系方式。完全可选。'
                )}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {l('Name', 'အမည်', '姓名')}
                  </label>
                  <input
                    type="text"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    placeholder={l('Your name (optional)', 'သင်၏ အမည် (ရွေးချယ်စရာ)', '您的姓名（可选）')}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('email')}
                  </label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder={l('your@email.com (optional)', 'your@email.com (ရွေးချယ်စရာ)', 'your@email.com（可选）')}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground font-bold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? l('Submitting...', 'ပေးပို့နေသည်...', '提交中...')
                  : l('Submit Feedback', 'တုံ့ပြန်ချက် ပေးပို့ရန်', '提交反馈')
                }
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-8 py-4 border-2 border-border text-foreground font-semibold rounded-xl hover:bg-muted transition-all duration-300"
              >
                {l('Cancel', 'ပယ်ဖျက်ရန်', '取消')}
              </button>
            </div>
          </form>

          {/* Info Box */}
          <div className="mt-8 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-muted-foreground">
              {l(
                '💡 Your feedback helps us improve the platform. All feedback is reviewed regularly, and we appreciate every suggestion!',
                '💡 သင်၏ တုံ့ပြန်ချက်သည် ပလက်ဖောင်းကို မြှင့်တင်ရန် အကူအညီပေးပါသည်။ တုံ့ပြန်ချက် အားလုံးကို ပုံမှန် ပြန်လည် စစ်ဆေးပြီး အကြံပြုချက် တစ်ခုစီကို တန်ဖိုးထားပါသည်။',
                '💡 您的反馈帮助我们改进平台。我们会定期审阅所有反馈，感谢每一条建议！'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
